import AsyncStorage from '@react-native-async-storage/async-storage';
let _profUserId = 'default'; try { require('@react-native-async-storage/async-storage').default.getItem('userToken').then(function(t){if(t)_profUserId=t.slice(0,16);}).catch(function(){}); } catch(e) {}
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef } from 'react';
import {
  View,
  Text,
  TextInput,
  TouchableOpacity,
  ScrollView,
  ActivityIndicator,
  StyleSheet,
  Dimensions,
  RefreshControl,
  Animated,
  StatusBar,
  Alert,
  Modal,
  KeyboardAvoidingView,
  Platform,
  SafeAreaView,
  FlatList,
} from 'react-native';
import { useNavigation, useFocusEffect } from '@react-navigation/native';
import portfolioDatabase from '../../services/PortfolioDatabase';
import { apiFetch } from '../../utils/apiClient';
import useProfile from '../../hooks/useProfile';
import ProfileManagementService from '../../services/ProfileManagementService';
import PROFILE_KEYS from '../../constants/profileKeys';

// === API CONFIG ===
const API_BASE = 'https://api.jobeng.ai';
const API_KEY = '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef';
// Bulletproof icon imports with fallbacks
const FallbackIcon = ({ name, size = 24, color = '#666', style, ...props }) => (
  <View 
    style={[
      {
        width: size,
        height: size,
        backgroundColor: color,
        borderRadius: size / 2,
        opacity: 0.3
      },
      style
    ]}
    {...props}
  />
);

let Ionicons = FallbackIcon;
let FontAwesome5 = FallbackIcon;
let MaterialIcons = FallbackIcon;

try {
  const iconsModule = require('@expo/vector-icons');
  if (iconsModule) {
    if (iconsModule.Ionicons && typeof iconsModule.Ionicons === 'function') {
      Ionicons = iconsModule.Ionicons;
    }
    if (iconsModule.FontAwesome5 && typeof iconsModule.FontAwesome5 === 'function') {
      FontAwesome5 = iconsModule.FontAwesome5;
    }
    if (iconsModule.MaterialIcons && typeof iconsModule.MaterialIcons === 'function') {
      MaterialIcons = iconsModule.MaterialIcons;
    }
  }
} catch (error) {
  console.warn('@expo/vector-icons not available, using fallback');
}

const { width, height } = Dimensions.get('window');

export default function AllSkillsScreen() {
  // ── Profile integration (auto-injected) ──
  const profileCtx = useProfile();
  // === Live API Data ===
  const [userId, setUserId] = React.useState('default');
  React.useEffect(() => {
    AsyncStorage.getItem('userToken').then(t => { if (t) setUserId(t.slice(0, 16)); }).catch(() => {});
  }, []);
  React.useEffect(() => {
    if (userId === 'default') return;
    const _ck = `professional_${_profUserId}_allskillsscreen`;
    apiFetch('/api/data/customers?limit=1').then(d => {
      if (d && !d.error) {
        const arr = Array.isArray(d) ? d : (d.data || d.results || []);
        AsyncStorage.setItem(_ck, JSON.stringify(arr)).catch(() => {});
      }
    }).catch(() => {});
  }, [userId]);

  const navigation = useNavigation();

  // Data states
  const [skills, setSkills] = useState([]);
  const [filteredSkills, setFilteredSkills] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [refreshing, setRefreshing] = useState(false);

  // UI states
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('All');
  const [selectedLevel, setSelectedLevel] = useState('All');
  const [sortBy, setSortBy] = useState('proficiency');
  const [showFilters, setShowFilters] = useState(false);

  // Edit/Add states
  const [showEditModal, setShowEditModal] = useState(false);
  const [editMode, setEditMode] = useState('add');
  const [editingSkill, setEditingSkill] = useState(null);
  const [formData, setFormData] = useState({});
  const [formErrors, setFormErrors] = useState({});

  // Animation
  const fadeAnim = useRef(new Animated.Value(0)).current;

  // Categories and levels
  const categories = ['All', 'Technical', 'Programming', 'Design', 'Management', 'Communication', 'Other'];
  const levels = ['All', 'Beginner', 'Intermediate', 'Advanced', 'Expert'];

  // Load data when screen comes into focus
  useFocusEffect(
    React.useCallback(() => {
      loadSkills();
    }, [])
  );

  // Set up event listeners
  useEffect(() => {
    const setupEventListeners = () => {
      portfolioDatabase.subscribe('skill_added', (skill) => {
        setSkills(prev => [skill, ...prev]);
      });

      portfolioDatabase.subscribe('skill_updated', (skill) => {
        setSkills(prev => prev.map(s => s.id === skill.id ? skill : s));
      });

      portfolioDatabase.subscribe('skill_deleted', ({ id }) => {
        setSkills(prev => prev.filter(s => s.id !== id));
      });
    };

    if (portfolioDatabase.initialized) {
      setupEventListeners();
    }

    return () => {
      // Cleanup if needed
    };
  }, []);

  // Apply filters and search
  useEffect(() => {
    applyFiltersAndSearch();
  }, [skills, searchQuery, selectedCategory, selectedLevel, sortBy]);

  // Start animations
  useEffect(() => {
    if (!isLoading) {
      Animated.timing(fadeAnim, {
        toValue: 1,
        duration: 600,
        useNativeDriver: true,
      }).start();
    }
  }, [isLoading]);

  const loadSkills = async () => {
    try {
      setIsLoading(true);
      
      // Initialize database if not already done
      if (!portfolioDatabase.initialized) {
        await portfolioDatabase.initialize();
      }

      const skillsData = await portfolioDatabase.getSkills();
      setSkills(skillsData || []);
      
    } catch (error) {
      console.error('❌ Error loading skills:', error);
      Alert.alert('Error', 'Failed to load skills. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  const applyFiltersAndSearch = () => {
    let filtered = [...skills];

    // Apply search
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(skill =>
        String(skill.name ?? '').toLowerCase().includes(query) ||
        String(skill.description ?? '').toLowerCase().includes(query) ||
        String(skill.category ?? '').toLowerCase().includes(query)
      );
    }

    // Apply category filter
    if (selectedCategory !== 'All') {
      filtered = filtered.filter(skill => 
        skill.category === selectedCategory
      );
    }

    // Apply level filter
    if (selectedLevel !== 'All') {
      const levelRanges = {
        'Beginner': [0, 25],
        'Intermediate': [26, 50],
        'Advanced': [51, 75],
        'Expert': [76, 100]
      };
      
      if (levelRanges[selectedLevel]) {
        const [min, max] = levelRanges[selectedLevel];
        filtered = filtered.filter(skill => 
          skill.proficiency >= min && skill.proficiency <= max
        );
      }
    }

    // Apply sorting
    filtered.sort((a, b) => {
      switch (sortBy) {
        case 'name':
          return a.name.localeCompare(b.name);
        case 'category':
          return a.category.localeCompare(b.category);
        case 'experience':
          return (b.yearsExperience || 0) - (a.yearsExperience || 0);
        case 'proficiency':
        default:
          return b.proficiency - a.proficiency;
      }
    });

    setFilteredSkills(filtered);
  };

  const onRefresh = async () => {
    setRefreshing(true);
    try {
      await loadSkills();
    } catch (error) {
      console.error('❌ Error refreshing:', error);
    } finally {
      setRefreshing(false);
    }
  };

  // Form validation
  const validateForm = (data) => {
    const errors = {};

    if (!data.name?.trim()) errors.name = 'Skill name is required';
    if (!data.proficiency || data.proficiency < 1 || data.proficiency > 100) {
      errors.proficiency = 'Proficiency must be between 1-100';
    }
    if (!data.description?.trim()) errors.description = 'Description is required';
    if (!data.category?.trim()) errors.category = 'Category is required';

    return errors;
  };

  // Handle add skill
  const handleAddSkill = () => {
    setEditMode('add');
    setEditingSkill(null);
    setFormData({
      name: '',
      proficiency: 50,
      description: '',
      category: 'Technical',
      yearsExperience: 1,
      projects: [],
      certifications: [],
      image: 'https://via.placeholder.com/300/0e4e96/FFFFFF?text=Skill'
    });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle edit skill
  const handleEditSkill = (skill) => {
    setEditMode('edit');
    setEditingSkill(skill);
    setFormData({ ...skill });
    setFormErrors({});
    setShowEditModal(true);
  };

  // Handle delete skill
  const handleDeleteSkill = (skill) => {
    Alert.alert(
      'Delete Skill',
      `Are you sure you want to delete "${skill.name}"? This action cannot be undone.`,
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Delete',
          style: 'destructive',
          onPress: async () => {
            try {
              const success = await portfolioDatabase.deleteSkill(skill.id);
              if (success) {
                Alert.alert('Success', 'Skill deleted successfully');
              } else {
                Alert.alert('Error', 'Failed to delete skill');
              }
            } catch (error) {
              console.error('❌ Delete error:', error);
              Alert.alert('Error', 'Failed to delete skill');
            }
          }
        }
      ]
    );
  };

  // Handle save skill
  const handleSaveSkill = async () => {
    const errors = validateForm(formData);
    setFormErrors(errors);

    if (Object.keys(errors).length > 0) {
      Alert.alert('Validation Error', 'Please fix the errors in the form');
      return;
    }

    try {
      let result = null;

      if (editMode === 'add') {
        result = await portfolioDatabase.addSkill(formData);
      } else {
        result = await portfolioDatabase.updateSkill(editingSkill.id, formData);
      }

      if (result) {
        setShowEditModal(false);
        Alert.alert('Success', `Skill ${editMode === 'add' ? 'added' : 'updated'} successfully`);
      } else {
        Alert.alert('Error', `Failed to ${editMode} skill`);
      }
    } catch (error) {
      console.error('❌ Save error:', error);
      Alert.alert('Error', `Failed to ${editMode} skill`);
    }
  };

  // Get skill level text
  const getSkillLevel = (proficiency) => {
    if (proficiency <= 25) return 'Beginner';
    if (proficiency <= 50) return 'Intermediate';
    if (proficiency <= 75) return 'Advanced';
    return 'Expert';
  };

  // Get skill level color
  const getSkillLevelColor = (proficiency) => {
    if (proficiency <= 25) return '#94A3B8';
    if (proficiency <= 50) return '#F59E0B';
    if (proficiency <= 75) return '#0e4e96';
    return '#10B981';
  };

  // Render skill card
  const renderSkillCard = ({ item: skill }) => (
    <TouchableOpacity
      style={styles.skillCard}
      onPress={() => {
        Alert.alert(
          skill.name,
          skill.description,
          [
            { text: 'Edit', onPress: () => handleEditSkill(skill) },
            { text: 'Delete', onPress: () => handleDeleteSkill(skill), style: 'destructive' },
            { text: 'Close', style: 'cancel' }
          ]
        );
      }}
      onLongPress={() => {
        Alert.alert(
          'Skill Options',
          `What would you like to do with "${skill.name}"?`,
          [
            { text: 'Edit', onPress: () => handleEditSkill(skill) },
            { text: 'Delete', onPress: () => handleDeleteSkill(skill), style: 'destructive' },
            { text: 'Cancel', style: 'cancel' }
          ]
        );
      }}
    >
      <View style={styles.skillHeader}>
        <View style={styles.skillInfo}>
          <Text style={styles.skillName}>{skill.name}</Text>
          <Text style={styles.skillCategory}>{skill.category}</Text>
        </View>
        <View style={styles.skillActions}>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => handleEditSkill(skill)}
          >
            <Ionicons name="create-outline" size={16} color="#0e4e96" />
          </TouchableOpacity>
          <TouchableOpacity
            style={styles.actionButton}
            onPress={() => handleDeleteSkill(skill)}
          >
            <Ionicons name="trash-outline" size={16} color="#EF4444" />
          </TouchableOpacity>
        </View>
      </View>

      <Text style={styles.skillDescription} numberOfLines={2}>
        {skill.description}
      </Text>

      <View style={styles.skillProgress}>
        <View style={styles.skillProgressHeader}>
          <Text style={styles.skillProficiencyText}>
            {skill.proficiency}% • {getSkillLevel(skill.proficiency)}
          </Text>
          <Text style={styles.skillExperienceText}>
            {skill.yearsExperience || 0} years
          </Text>
        </View>
        <View style={styles.skillProgressBar}>
          <View
            style={[
              styles.skillProgressFill,
              {
                width: `${skill.proficiency}%`,
                backgroundColor: getSkillLevelColor(skill.proficiency)
              }
            ]}
          />
        </View>
      </View>

      {skill.projects && skill.projects.length > 0 && (
        <View style={styles.skillProjects}>
          <Text style={styles.skillProjectsText}>
            Used in {skill.projects.length} project{skill.projects.length !== 1 ? 's' : ''}
          </Text>
        </View>
      )}
    </TouchableOpacity>
  );

  // Render filters modal
  const renderFiltersModal = () => (
    <Modal
      visible={showFilters}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowFilters(false)}
    >
      <View style={styles.filtersModalContainer}>
        <View style={styles.filtersModalContent}>
          <View style={styles.filtersModalHeader}>
            <Text style={styles.filtersModalTitle}>Filters & Sort</Text>
            <TouchableOpacity
              style={styles.filtersModalCloseButton}
              onPress={() => setShowFilters(false)}
            >
              <Ionicons name="close" size={24} color="#0F172A" />
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.filtersModalBody}>
            {/* Category Filter */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Category</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterOptions}>
                  {categories.map((category) => (
                    <TouchableOpacity
                      key={category}
                      style={[
                        styles.filterOption,
                        selectedCategory === category && styles.filterOptionSelected
                      ]}
                      onPress={() => setSelectedCategory(category)}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        selectedCategory === category && styles.filterOptionTextSelected
                      ]}>
                        {category}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Level Filter */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Skill Level</Text>
              <ScrollView horizontal showsHorizontalScrollIndicator={false}>
                <View style={styles.filterOptions}>
                  {levels.map((level) => (
                    <TouchableOpacity
                      key={level}
                      style={[
                        styles.filterOption,
                        selectedLevel === level && styles.filterOptionSelected
                      ]}
                      onPress={() => setSelectedLevel(level)}
                    >
                      <Text style={[
                        styles.filterOptionText,
                        selectedLevel === level && styles.filterOptionTextSelected
                      ]}>
                        {level}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
              </ScrollView>
            </View>

            {/* Sort Options */}
            <View style={styles.filterSection}>
              <Text style={styles.filterSectionTitle}>Sort By</Text>
              <View style={styles.sortOptions}>
                {[
                  { key: 'proficiency', label: 'Proficiency' },
                  { key: 'name', label: 'Name' },
                  { key: 'category', label: 'Category' },
                  { key: 'experience', label: 'Experience' }
                ].map((option) => (
                  <TouchableOpacity
                    key={option.key}
                    style={[
                      styles.sortOption,
                      sortBy === option.key && styles.sortOptionSelected
                    ]}
                    onPress={() => setSortBy(option.key)}
                  >
                    <Text style={[
                      styles.sortOptionText,
                      sortBy === option.key && styles.sortOptionTextSelected
                    ]}>
                      {option.label}
                    </Text>
                  </TouchableOpacity>
                ))}
              </View>
            </View>
          </ScrollView>

          <View style={styles.filtersModalFooter}>
            <TouchableOpacity
              style={styles.clearFiltersButton}
              onPress={() => {
                setSelectedCategory('All');
                setSelectedLevel('All');
                setSortBy('proficiency');
              }}
            >
              <Text style={styles.clearFiltersButtonText}>Clear All</Text>
            </TouchableOpacity>
            <TouchableOpacity
              style={styles.applyFiltersButton}
              onPress={() => setShowFilters(false)}
            >
              <Text style={styles.applyFiltersButtonText}>Apply</Text>
            </TouchableOpacity>
          </View>
        </View>
      </View>
    </Modal>
  );

  // Render edit modal
  const renderEditModal = () => (
    <Modal
      visible={showEditModal}
      transparent={true}
      animationType="slide"
      onRequestClose={() => setShowEditModal(false)}
    >
      <KeyboardAvoidingView
        style={styles.editModalContainer}
        behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
      >
        <View style={styles.editModalContent}>
          <View style={styles.editModalHeader}>
            <TouchableOpacity
              style={styles.editModalCloseButton}
              onPress={() => setShowEditModal(false)}
            >
              <Ionicons name="close" size={24} color="#0F172A" />
            </TouchableOpacity>
            <Text style={styles.editModalTitle}>
              {editMode === 'add' ? 'Add Skill' : 'Edit Skill'}
            </Text>
            <TouchableOpacity
              style={styles.editModalSaveButton}
              onPress={handleSaveSkill}
            >
              <Text style={styles.editModalSaveButtonText}>Save</Text>
            </TouchableOpacity>
          </View>

          <ScrollView style={styles.editModalBody} showsVerticalScrollIndicator={false}>
            <View style={styles.formContainer}>
              {/* Skill Name */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Skill Name *</Text>
                <TextInput
                  style={[styles.formInput, formErrors.name && styles.formInputError]}
                  value={formData.name || ''}
                  onChangeText={(text) => setFormData({ ...formData, name: text })}
                  placeholder="Enter skill name"
                  placeholderTextColor="#94A3B8"
                />
                {formErrors.name && <Text style={styles.formErrorText}>{formErrors.name}</Text>}
              </View>

              {/* Description */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Description *</Text>
                <TextInput
                  style={[styles.formTextArea, formErrors.description && styles.formInputError]}
                  value={formData.description || ''}
                  onChangeText={(text) => setFormData({ ...formData, description: text })}
                  placeholder="Describe your skill and experience"
                  placeholderTextColor="#94A3B8"
                  multiline
                  numberOfLines={4}
                />
                {formErrors.description && <Text style={styles.formErrorText}>{formErrors.description}</Text>}
              </View>

              {/* Category */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Category *</Text>
                <View style={styles.formPickerContainer}>
                  {categories.filter(cat => cat !== 'All').map((category) => (
                    <TouchableOpacity
                      key={category}
                      style={[
                        styles.formPickerOption,
                        formData.category === category && styles.formPickerOptionSelected
                      ]}
                      onPress={() => setFormData({ ...formData, category })}
                    >
                      <Text style={[
                        styles.formPickerOptionText,
                        formData.category === category && styles.formPickerOptionTextSelected
                      ]}>
                        {category}
                      </Text>
                    </TouchableOpacity>
                  ))}
                </View>
                {formErrors.category && <Text style={styles.formErrorText}>{formErrors.category}</Text>}
              </View>

              {/* Proficiency */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>
                  Proficiency: {formData.proficiency || 50}% • {getSkillLevel(formData.proficiency || 50)}
                </Text>
                <View style={styles.proficiencySliderContainer}>
                  <View style={styles.proficiencySlider}>
                    <View
                      style={[
                        styles.proficiencySliderFill,
                        {
                          width: `${formData.proficiency || 50}%`,
                          backgroundColor: getSkillLevelColor(formData.proficiency || 50)
                        }
                      ]}
                    />
                  </View>
                  <View style={styles.proficiencyButtons}>
                    <TouchableOpacity
                      style={styles.proficiencyButton}
                      onPress={() => setFormData({ 
                        ...formData, 
                        proficiency: Math.max(0, (formData.proficiency || 50) - 5) 
                      })}
                    >
                      <Ionicons name="remove" size={16} color="#64748B" />
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.proficiencyButton}
                      onPress={() => setFormData({ 
                        ...formData, 
                        proficiency: Math.min(100, (formData.proficiency || 50) + 5) 
                      })}
                    >
                      <Ionicons name="add" size={16} color="#64748B" />
                    </TouchableOpacity>
                  </View>
                </View>
                {formErrors.proficiency && <Text style={styles.formErrorText}>{formErrors.proficiency}</Text>}
              </View>

              {/* Years of Experience */}
              <View style={styles.formField}>
                <Text style={styles.formLabel}>Years of Experience</Text>
                <TextInput
                  style={styles.formInput}
                  value={String(formData.yearsExperience || 1)}
                  onChangeText={(text) => setFormData({ 
                    ...formData, 
                    yearsExperience: parseInt(text) || 1 
                  })}
                  placeholder="Years of experience"
                  placeholderTextColor="#94A3B8"
                  keyboardType="numeric"
                />
              </View>
            </View>
          </ScrollView>
        </View>
      </KeyboardAvoidingView>
    </Modal>
  );

  // Render loading screen
  if (isLoading) {
    return (
      <SafeAreaView style={styles.safeArea}>
        <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />
        
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Ionicons name="arrow-back" size={24} color="#ffffff" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>All Skills</Text>
          <View style={{ width: 32 }} />
        </View>

        <View style={styles.loadingContainer}>
          <ActivityIndicator size={36} color="#0e4e96" />
          <Text style={styles.loadingText}>Loading skills...</Text>
        </View>
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.safeArea}>
      <StatusBar barStyle="light-content" backgroundColor="#0e4e96" />

      {/* Header */}
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Ionicons name="arrow-back" size={24} color="#ffffff" />
        </TouchableOpacity>
        <Text style={styles.headerTitle}>All Skills</Text>
        <TouchableOpacity
          style={styles.addButton}
          onPress={handleAddSkill}
        >
          <Ionicons name="add" size={24} color="#ffffff" />
        </TouchableOpacity>
      </View>

      <Animated.View style={[styles.content, { opacity: fadeAnim }]}>
        {/* Search and Filters */}
        <View style={styles.searchContainer}>
          <View style={styles.searchBar}>
            <Ionicons name="search" size={20} color="#64748B" />
            <TextInput
              style={styles.searchInput}
              placeholder="Search skills..."
              value={searchQuery}
              onChangeText={setSearchQuery}
              placeholderTextColor="#94A3B8"
            />
            {searchQuery.length > 0 && (
              <TouchableOpacity onPress={() => setSearchQuery('')}>
                <Ionicons name="close-circle" size={20} color="#64748B" />
              </TouchableOpacity>
            )}
          </View>
          <TouchableOpacity
            style={styles.filterButton}
            onPress={() => setShowFilters(true)}
          >
            <Ionicons name="filter" size={20} color="#0e4e96" />
          </TouchableOpacity>
        </View>

        {/* Active Filters */}
        {(selectedCategory !== 'All' || selectedLevel !== 'All') && (
          <View style={styles.activeFiltersContainer}>
            <ScrollView horizontal showsHorizontalScrollIndicator={false}>
              {selectedCategory !== 'All' && (
                <View style={styles.activeFilter}>
                  <Text style={styles.activeFilterText}>{selectedCategory}</Text>
                  <TouchableOpacity onPress={() => setSelectedCategory('All')}>
                    <Ionicons name="close" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                </View>
              )}
              {selectedLevel !== 'All' && (
                <View style={styles.activeFilter}>
                  <Text style={styles.activeFilterText}>{selectedLevel}</Text>
                  <TouchableOpacity onPress={() => setSelectedLevel('All')}>
                    <Ionicons name="close" size={16} color="#0e4e96" />
                  </TouchableOpacity>
                </View>
              )}
            </ScrollView>
          </View>
        )}

        {/* Skills List */}
        <FlatList
          data={filteredSkills}
          renderItem={renderSkillCard}
          keyExtractor={item => item.id}
          contentContainerStyle={styles.skillsList}
          showsVerticalScrollIndicator={false}
          refreshControl={
            <RefreshControl refreshing={refreshing} onRefresh={onRefresh} />
          }
          ListEmptyComponent={
            <View style={styles.emptyContainer}>
              <Ionicons name="star-outline" size={60} color="#CBD5E1" />
              <Text style={styles.emptyText}>No skills found</Text>
              <Text style={styles.emptySubtext}>
                {searchQuery || selectedCategory !== 'All' || selectedLevel !== 'All'
                  ? 'Try adjusting your search or filters'
                  : 'Add your first skill to get started'}
              </Text>
              <TouchableOpacity 
                style={styles.addFirstSkillButton}
                onPress={handleAddSkill}
              >
                <Text style={styles.addFirstSkillButtonText}>Add Skill</Text>
              </TouchableOpacity>
            </View>
          }
        />
      </Animated.View>

      {/* Modals */}
      {renderFiltersModal()}
      {renderEditModal()}
    </SafeAreaView>
  );
}

const styles = StyleSheet.create({
  safeArea: {
    flex: 1,
    backgroundColor: '#0e4e96',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#0e4e96',
  },
  backButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  addButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: 'rgba(255, 255, 255, 0.1)',
    alignItems: 'center',
    justifyContent: 'center',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#ffffff',
    textAlign: 'center',
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: '#F8FAFC',
  },
  loadingText: {
    marginTop: 16,
    fontSize: 16,
    color: '#64748B',
    fontWeight: '500',
  },
  content: {
    flex: 1,
    backgroundColor: '#F8FAFC',
  },
  searchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    paddingHorizontal: 20,
    paddingVertical: 16,
    backgroundColor: '#ffffff',
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  searchBar: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#F1F5F9',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 12,
    marginRight: 12,
  },
  searchInput: {
    flex: 1,
    fontSize: 16,
    color: '#0F172A',
    marginLeft: 12,
  },
  filterButton: {
    width: 44,
    height: 44,
    borderRadius: 12,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  activeFiltersContainer: {
    backgroundColor: '#ffffff',
    paddingHorizontal: 20,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  activeFilter: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#0e4e96' + '20',
    borderRadius: 16,
    paddingHorizontal: 12,
    paddingVertical: 6,
    marginRight: 8,
  },
  activeFilterText: {
    fontSize: 12,
    color: '#0e4e96',
    fontWeight: '500',
    marginRight: 6,
  },
  skillsList: {
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  skillCard: {
    backgroundColor: '#ffffff',
    borderRadius: 16,
    marginBottom: 16,
    padding: 20,
    shadowColor: '#000',
    shadowOffset: { width: 0, height: 2 },
    shadowOpacity: 0.1,
    shadowRadius: 8,
    elevation: 4,
  },
  skillHeader: {
    flexDirection: 'row',
    alignItems: 'flex-start',
    justifyContent: 'space-between',
    marginBottom: 12,
  },
  skillInfo: {
    flex: 1,
  },
  skillName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 4,
  },
  skillCategory: {
    fontSize: 14,
    color: '#64748B',
  },
  skillActions: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
    marginLeft: 8,
  },
  skillDescription: {
    fontSize: 14,
    color: '#64748B',
    lineHeight: 20,
    marginBottom: 16,
  },
  skillProgress: {
    marginBottom: 12,
  },
  skillProgressHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    marginBottom: 8,
  },
  skillProficiencyText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
  },
  skillExperienceText: {
    fontSize: 12,
    color: '#64748B',
  },
  skillProgressBar: {
    height: 8,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
  },
  skillProgressFill: {
    height: '100%',
    borderRadius: 4,
  },
  skillProjects: {
    marginTop: 8,
  },
  skillProjectsText: {
    fontSize: 12,
    color: '#64748B',
    fontStyle: 'italic',
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    paddingVertical: 60,
  },
  emptyText: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
    marginTop: 16,
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#64748B',
    textAlign: 'center',
    lineHeight: 20,
    marginBottom: 24,
  },
  addFirstSkillButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 24,
    paddingVertical: 12,
    borderRadius: 8,
  },
  addFirstSkillButtonText: {
    color: '#ffffff',
    fontSize: 14,
    fontWeight: '600',
  },
  // Filters Modal Styles
  filtersModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  filtersModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.7,
  },
  filtersModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  filtersModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  filtersModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  filtersModalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  filterSection: {
    marginVertical: 16,
  },
  filterSectionTitle: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 12,
  },
  filterOptions: {
    flexDirection: 'row',
  },
  filterOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    marginRight: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  filterOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  filterOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  filterOptionTextSelected: {
    color: '#ffffff',
  },
  sortOptions: {
    flexDirection: 'row',
    flexWrap: 'wrap',
  },
  sortOption: {
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    marginRight: 8,
    marginBottom: 8,
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
  sortOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  sortOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  sortOptionTextSelected: {
    color: '#ffffff',
  },
  filtersModalFooter: {
    flexDirection: 'row',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderTopWidth: 1,
    borderTopColor: '#E2E8F0',
  },
  clearFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    marginRight: 8,
  },
  clearFiltersButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#64748B',
  },
  applyFiltersButton: {
    flex: 1,
    paddingVertical: 12,
    borderRadius: 8,
    backgroundColor: '#0e4e96',
    alignItems: 'center',
    marginLeft: 8,
  },
  applyFiltersButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  // Edit Modal Styles
  editModalContainer: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  editModalContent: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.9,
    minHeight: height * 0.6,
  },
  editModalHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingHorizontal: 20,
    paddingVertical: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#E2E8F0',
  },
  editModalCloseButton: {
    width: 32,
    height: 32,
    borderRadius: 16,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
  },
  editModalTitle: {
    fontSize: 18,
    fontWeight: '600',
    color: '#0F172A',
  },
  editModalSaveButton: {
    backgroundColor: '#0e4e96',
    paddingHorizontal: 16,
    paddingVertical: 8,
    borderRadius: 8,
  },
  editModalSaveButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#ffffff',
  },
  editModalBody: {
    flex: 1,
    paddingHorizontal: 20,
  },
  formContainer: {
    paddingVertical: 20,
  },
  formField: {
    marginBottom: 20,
  },
  formLabel: {
    fontSize: 14,
    fontWeight: '600',
    color: '#0F172A',
    marginBottom: 8,
  },
  formInput: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#ffffff',
  },
  formInputError: {
    borderColor: '#EF4444',
  },
  formTextArea: {
    borderWidth: 1,
    borderColor: '#E2E8F0',
    borderRadius: 8,
    paddingHorizontal: 16,
    paddingVertical: 12,
    fontSize: 16,
    color: '#0F172A',
    backgroundColor: '#ffffff',
    minHeight: 100,
    textAlignVertical: 'top',
  },
  formErrorText: {
    fontSize: 12,
    color: '#EF4444',
    marginTop: 4,
  },
  formPickerContainer: {
    flexDirection: 'row',
    flexWrap: 'wrap',
    gap: 8,
  },
  formPickerOption: {
    paddingHorizontal: 12,
    paddingVertical: 8,
    borderRadius: 6,
    borderWidth: 1,
    borderColor: '#E2E8F0',
    backgroundColor: '#ffffff',
  },
  formPickerOptionSelected: {
    backgroundColor: '#0e4e96',
    borderColor: '#0e4e96',
  },
  formPickerOptionText: {
    fontSize: 14,
    color: '#64748B',
    fontWeight: '500',
  },
  formPickerOptionTextSelected: {
    color: '#ffffff',
  },
  proficiencySliderContainer: {
    marginTop: 8,
  },
  proficiencySlider: {
    height: 8,
    backgroundColor: '#E2E8F0',
    borderRadius: 4,
    marginBottom: 12,
  },
  proficiencySliderFill: {
    height: '100%',
    borderRadius: 4,
  },
  proficiencyButtons: {
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 16,
  },
  proficiencyButton: {
    width: 40,
    height: 40,
    borderRadius: 20,
    backgroundColor: '#F1F5F9',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 1,
    borderColor: '#E2E8F0',
  },
});
